import { useState } from 'react'
import type { AuditLogRow } from '@alpha-edge/shared'
import { AUDIT_ACTIONS } from '@alpha-edge/shared'
import { PageHeader, DataTable, Modal, ColumnSettingsButton, colors, input, btnSecondary } from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useTableColumns } from '../../hooks/use-table-columns'
import { relativeTime } from '../fleet/fleet-helpers'

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const ACTION_COLORS: Record<string, { bg: string; text: string }> = {
  create: { bg: '#d1fae5', text: '#047857' },
  update: { bg: '#dbeafe', text: '#1d4ed8' },
  delete: { bg: '#fee2e2', text: '#b91c1c' },
  archive: { bg: '#fef9c3', text: '#a16207' },
  purge: { bg: '#fecaca', text: '#7f1d1d' },
}

function ActionBadge({ action }: { readonly action: string }) {
  const c = ACTION_COLORS[action] ?? { bg: '#f3f4f6', text: '#6b7280' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: c.bg,
        color: c.text,
        textTransform: 'capitalize',
        letterSpacing: '0.3px',
      }}
    >
      {action.replace(/_/g, ' ')}
    </span>
  )
}

function jsonBlock(val: Record<string, unknown> | null) {
  return (
    <pre
      style={{
        margin: 0,
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        background: '#1e1e2e',
        color: '#cdd6f4',
        padding: '12px',
        borderRadius: '6px',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
      }}
    >
      {val ? JSON.stringify(val, null, 2) : '—'}
    </pre>
  )
}

function AuditDetailModal({ row, onClose }: { readonly row: AuditLogRow | null; readonly onClose: () => void }) {
  if (!row) return null
  return (
    <Modal isOpen={row !== null} onClose={onClose} title="Audit Event Detail" width="720px">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <tbody>
          {(
            [
              ['Timestamp', new Date(row.timestamp).toLocaleString()],
              ['Action', <ActionBadge key="a" action={row.action} />],
              ['Entity Type', row.entityType],
              ['Entity ID', row.entityId],
              ['Changed By', row.changedByName ?? row.changedById ?? '—'],
              ['Impersonator', row.impersonatorName ?? row.impersonatorId ?? '—'],
              ['IP Address', row.ipAddress ?? '—'],
              ['Source', row.edgeDeviceId ? `edge: ${row.edgeDeviceId}` : 'server'],
              ['Old Values', jsonBlock(row.oldValues)],
              ['New Values', jsonBlock(row.newValues)],
            ] as Array<[string, React.ReactNode]>
          ).map(([k, v]) => (
            <tr key={k} style={{ borderBottom: `1px solid ${colors.border}` }}>
              <td
                style={{
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: colors.textMuted,
                  width: '140px',
                  verticalAlign: 'top',
                  whiteSpace: 'nowrap',
                }}
              >
                {k}
              </td>
              <td style={{ padding: '8px 12px', color: colors.text, wordBreak: 'break-all' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  )
}

export function AuditTrailPage() {
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [changedBy, setChangedBy] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [detailRow, setDetailRow] = useState<AuditLogRow | null>(null)
  const { tableId: _, ...tableColumnProps } = useTableColumns('audit-trail')

  const filters: Record<string, string | undefined> = {
    entityType: entityType || undefined,
    action: action || undefined,
    changedBy: changedBy || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
  } = useApiList<AuditLogRow>({
    path: '/admin/audit',
    limit: 50,
    filters,
  })

  const hasFilters = Boolean(entityType || action || changedBy || from || to)
  const clearFilters = () => {
    setEntityType('')
    setAction('')
    setChangedBy('')
    setFrom('')
    setTo('')
  }

  const columns: readonly Column<AuditLogRow>[] = [
    { key: 'timestamp', header: 'Timestamp', render: (r) => relativeTime(r.timestamp), sortable: true },
    { key: 'action', header: 'Action', render: (r) => <ActionBadge action={r.action} />, sortable: true },
    { key: 'entityType', header: 'Entity', render: (r) => r.entityType, sortable: true, filterable: true },
    {
      key: 'entityId',
      header: 'Entity ID',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.entityId}</span>,
    },
    {
      key: 'changedByName',
      header: 'Changed By',
      render: (r) => r.changedByName ?? r.changedById ?? '—',
      sortable: true,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Audit Trail"
        subtitle={`Operations audit log${total > 0 ? ` — ${total.toLocaleString()} events` : ''}`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Entity Type</label>
            <input
              type="text"
              placeholder="e.g. orders"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              style={{ ...input, width: '160px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>Action</label>
            <select value={action} onChange={(e) => setAction(e.target.value)} style={{ ...input, width: '170px' }}>
              <option value="">(all)</option>
              {AUDIT_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Changed By</label>
            <input
              type="text"
              placeholder="name or id"
              value={changedBy}
              onChange={(e) => setChangedBy(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
          <div>
            <label style={labelStyle}>From</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
          <div>
            <label style={labelStyle}>To</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ ...input, width: '200px' }}
            />
          </div>
        </div>
        {hasFilters && (
          <div style={{ marginBottom: '16px' }}>
            <button style={btnSecondary} onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage="No audit events found for the selected filters."
            onRowClick={(row) => setDetailRow(row)}
            {...tableColumnProps}
          />
        )}
      </div>
      <AuditDetailModal row={detailRow} onClose={() => setDetailRow(null)} />
    </div>
  )
}
